import StructuredData from '@components/app/StructuredData'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import { type ClinicalTrialForListingPage } from '@modules/trials/helpers/fragments/clinicalTrialListingFragment'
import getPrimaryTreatmentNameFromTrial from '@modules/trials/helpers/getPrimaryTreatmentNameFromTrial'
import { inferPrimaryConditionFromTrial } from '@modules/trials/helpers/inferPrimaryConditionFromTrial'
import type { TrialInDatabaseForAlgolia } from '@modules/trials/types/TrialInDatabaseForAlgolia'
import { jsonLdScriptProps } from 'react-schemaorg'
import type {
  AdministrativeArea,
  MedicalTrial,
  MedicalTrialDesign,
  Person,
} from 'schema-dts'

/**
 * Builds an string enum array conforming to https://schema.org/MedicalTrialDesign
 */
function buildTrialDesign(
  trial:
    | Pick<TrialInDatabaseForAlgolia, 'trialLocations'>
    | Pick<ClinicalTrialForListingPage, 'trialLocations'>,
) {
  const trialDesign: MedicalTrialDesign[] = [
    'RandomizedTrial',
    'PlaceboControlledTrial',
    'OpenTrial',
  ]
  if (trial.trialLocations.length > 1) {
    trialDesign.push('MultiCenterTrial')
  } else {
    trialDesign.push('SingleCenterTrial')
  }
  if (
    new Set(trial.trialLocations.map((location) => location.country)).size > 1
  ) {
    trialDesign.push('InternationalTrial')
  }

  return trialDesign
}

/**
 * Builds a "Person" entity as the responsible party for the trial. Handles null values.
 */
function buildSponsor(
  trial: Pick<
    ClinicalTrialForListingPage,
    | 'responsiblePartyInvestigatorFullName'
    | 'responsiblePartyInvestigatorTitle'
    | 'responsiblePartyInvestigatorAffiliation'
  >,
) {
  let person: Person = {
    '@type': 'Person',
  }
  if (trial.responsiblePartyInvestigatorFullName) {
    person.name = trial.responsiblePartyInvestigatorFullName
  }
  if (trial.responsiblePartyInvestigatorTitle) {
    person.jobTitle = trial.responsiblePartyInvestigatorTitle
  }
  if (trial.responsiblePartyInvestigatorAffiliation) {
    person.affiliation = trial.responsiblePartyInvestigatorAffiliation
  }

  return person
}

/**
 * Creates a list of locations, with the type as either AdministrativeArea or Place
 */
function buildStudyAdministrativeAreaList(
  trial: Pick<TrialInDatabaseForAlgolia, 'trialLocations'>,
): AdministrativeArea[] {
  const administrativeAreaList: AdministrativeArea[] = []

  trial.trialLocations.forEach((location) => {
    if (!location) {
      administrativeAreaList.push({
        '@type': 'AdministrativeArea',
        'address': 'Unknown',
      })
    } else {
      let studyLocation: AdministrativeArea = {
        '@type': 'AdministrativeArea',
        'address': [location.city, location.state, location.country]
          .filter((item) => !!item)
          .join(', '),
      }
      if (location.lat && location.lng) {
        studyLocation.latitude = location.lat
        studyLocation.longitude = location.lng
      }
      if (location.facility) {
        studyLocation.name = location.facility
      }
      administrativeAreaList.push(studyLocation)
    }
  })
  return administrativeAreaList
}

/**
 * Inject structured data describing this trial for Google. See StructureDataForDirectory for another implementation.
 * @deprecated Please use `StructuredDataForNewTrialListing` instead
 */
export default function StructuredDataForTrial({
  trial,
}: {
  trial: Pick<
    ClinicalTrialForListingPage,
    | 'briefSummary'
    | 'briefTitle'
    | 'conditions'
    | 'conjuredTitle'
    | 'nlpBriefSummary'
    | 'overallStatus'
    | 'responsiblePartyInvestigatorAffiliation'
    | 'responsiblePartyInvestigatorFullName'
    | 'responsiblePartyInvestigatorTitle'
    | 'slug'
    | 'treatments'
  > & {
    trialLocations: TrialInDatabaseForAlgolia['trialLocations']
  }
}) {
  return (
    <>
      <StructuredData
        id={`${trial.slug}-summary`}
        scriptTagProps={jsonLdScriptProps<MedicalTrial>({
          '@context': 'https://schema.org',
          '@type': 'MedicalTrial',
          'description': trial.nlpBriefSummary ?? trial.briefSummary,
          'healthCondition': {
            '@type': 'MedicalCondition',
            'name': inferPrimaryConditionFromTrial(trial) ?? 'Condition',
          },
          'medicineSystem': 'WesternConventional',
          'name': trial.briefTitle,
          'sponsor': buildSponsor(trial),
          'status': trial.overallStatus,
          'studyLocation': buildStudyAdministrativeAreaList(trial),
          'studySubject': {
            '@type': 'MedicalEntity',
            'name': getPrimaryTreatmentNameFromTrial(trial),
          },
          'trialDesign': buildTrialDesign(trial),
          'url': absoluteUrl(DynamicRoutes.trialNaked(trial.slug!)),
        })}
      />
    </>
  )
}
